import React, { Component } from 'react';
import { Button } from './common/Button';
import { Choice, PumpChoiceKeys } from './common/PumpChoice';
import { Breadcrumb } from './Breadcrumb';
import { Frequency, Horsepower, LEPPumpViewModel, PumpProperty } from './common/LEPPumpViewModel';
import { getGrinderHPValues, getSolidsHandlingHPValues } from '../bridgeAPIService';

interface IProps {
    choicesComplete?: (status: boolean, viewModel?: LEPPumpViewModel) => void;
    hideBreadcrumb: boolean;
}

interface IState {
    frequencyChoice: boolean;
    pumpTypeChoice: boolean;
    sealTypeChoice: boolean;
    solidsHandlingSizeChoice: boolean;
    impellerTypeChoice: boolean;
    locationChoice: boolean;
    horsepowerChoice: boolean;
    grinderSeriesChoice: boolean;
    grinderSelected: boolean;
    singleSealSelected: boolean;
    fiftyHzSelected: boolean;
    complete: boolean;
    pumpChoices: Choice[];
}

/**
 * This is the first component a user will interact with, and guides them
 * through choices to successfully start building an LEP pump.
 *
 * A diagram depicting the logic flow can be found here: https://libertypumps.atlassian.net/l/cp/CMeDi1w4
 */
export class PumpChoiceSelector extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.getDefaultGrinderSeries = this.getDefaultGrinderSeries.bind(this);

        this.selectFrequency = this.selectFrequency.bind(this);

        this.selectPumpType = this.selectPumpType.bind(this);
        this.autoSelectSealTypeIf50Hz = this.autoSelectSealTypeIf50Hz.bind(this);

        this.selectSealType = this.selectSealType.bind(this);
        this.autoSelectSizeForDualSealSolidsHandling = this.autoSelectSizeForDualSealSolidsHandling.bind(this);
        this.autoSelectOrdinaryLocationIf50HzGrinder = this.autoSelectOrdinaryLocationIf50HzGrinder.bind(this);

        this.selectSolidsHandlingSize = this.selectSolidsHandlingSize.bind(this);

        this.selectLocation = this.selectLocation.bind(this);
        this.autoSelectHorsepowerForSingleSealGrinder = this.autoSelectHorsepowerForSingleSealGrinder.bind(this);

        this.selectHorsepower = this.selectHorsepower.bind(this);
        this.addHighFlowGrinderSeriesOption = this.addHighFlowGrinderSeriesOption.bind(this);

        this.selectGrinderSeries = this.selectGrinderSeries.bind(this);

        this.tryFireChoicesCompleteEvent = this.tryFireChoicesCompleteEvent.bind(this);
        this.choiceMade = this.choiceMade.bind(this);
        this.navigateTo = this.navigateTo.bind(this);
        this.getViewModel = this.getViewModel.bind(this);
        this.getPumpType = this.getPumpType.bind(this);
        this.getHorsepower = this.getHorsepower.bind(this);
        this.getHPValues = this.getHPValues.bind(this);
        this.state = {
            frequencyChoice: true,
            pumpTypeChoice: false,
            sealTypeChoice: false,
            solidsHandlingSizeChoice: false,
            impellerTypeChoice: false,
            locationChoice: false,
            horsepowerChoice: false,
            grinderSeriesChoice: false,
            grinderSelected: false,
            singleSealSelected: false,
            fiftyHzSelected: false,
            complete: false,
            pumpChoices: []
        };
    }

    FrequencyButtonChoices: ChoiceButtonProps[] = [
        {
            text: '50Hz',
            eventHandler: () => {
                this.selectFrequency(true);
            }
        },
        {
            text: '60Hz',
            eventHandler: () => {
                this.selectFrequency(false);
            }
        }
    ];

    PumpTypeChoices: ChoiceButtonProps[] = [
        {
            text: 'Grinder',
            eventHandler: () => {
                this.selectPumpType(true, 'Grinder');
            }
        },
        {
            text: 'Solids Handling',
            eventHandler: () => {
                this.selectPumpType(false, 'Solids Handling');
            }
        }
    ];

    SealTypeChoices: ChoiceButtonProps[] = [
        {
            text: 'Single Seal',
            eventHandler: () => {
                this.selectSealType(true);
            }
        },
        {
            text: 'Dual Seal',
            eventHandler: () => {
                this.selectSealType(false);
            }
        }
    ];

    /**
     * 2.5" is only available to single seal pumps.
     */
    SolidsHandlingSizeChoices = (): ChoiceButtonProps[] => {
        const result: ChoiceButtonProps[] = [];
        if (this.state.singleSealSelected) {
            result.push({
                text: '2.5"',
                eventHandler: () => {
                    this.selectSolidsHandlingSize('2.5"');
                }
            });
        }
        result.push({
            text: '3"',
            eventHandler: () => {
                this.selectSolidsHandlingSize('3"');
            }
        });
        return result;
    }

    ImpellerTypeChoices: ChoiceButtonProps[] = [
        {
            text: 'MonoVane',
            eventHandler: () => {
                this.selectImpellerType('MonoVane');
            }
        },
        {
            text: 'Vortex',
            eventHandler: () => {
                this.selectImpellerType('Vortex');
            }
        },
        {
            text: '2-Vane',
            eventHandler: () => {
                this.selectImpellerType('2-Vane');
            }
        }
    ];

    LocationChoices: ChoiceButtonProps[] = [
        {
            text: 'Ordinary',
            eventHandler: () => {
                this.selectLocation(false);
            }
        },
        {
            text: 'Hazardous',
            eventHandler: () => {
                this.selectLocation(true);
            }
        }
    ];

    /**
     * Horsepower choices are filled out by an API call. To start, this should be empty.
     */
    HorsepowerChoices: ChoiceButtonProps[] = [];

    private getDefaultGrinderSeries(): ChoiceButtonProps[] {
        return [
            {
                text: 'LGV High Volume Grinder',
                eventHandler: () => {
                    this.selectGrinderSeries('LGV High Volume Grinder');
                }
            },
            {
                text: 'LGH High Head Grinder',
                eventHandler: () => {
                    this.selectGrinderSeries('LGH High Head Grinder');
                }
            }
        ];
    }

    GrinderSeriesChoices: ChoiceButtonProps[] = this.getDefaultGrinderSeries();

    /**
     * Event Handler for a frequency choice being made. Refer to logic diagram
     * for instruction on how to proceed.
     * @param fiftyHzSelected was the frequency choice 50hz?
     */
    private selectFrequency(fiftyHzSelected: boolean): void {
        this.choiceMade({ key: PumpChoiceKeys.FREQUENCY, value: fiftyHzSelected ? '50Hz' : '60Hz' });
        this.setState({
            frequencyChoice: false,
            pumpTypeChoice: true,
            fiftyHzSelected: fiftyHzSelected
        });
    }

    /**
     * Event Handler for a pump type choice being made. Refer to logic diagram
     * for instruction on how to proceed.
     * @param grinder was the pump type a grinder?
     * @param value the pump type that was chosen
     */
    private selectPumpType(grinder: boolean, value: string): void {
        this.choiceMade({ key: PumpChoiceKeys.PUMP_TYPE, value: value });
        this.setState({
            pumpTypeChoice: false,
            sealTypeChoice: true,
            grinderSelected: grinder
        }, this.autoSelectSealTypeIf50Hz);
    }

    /**
     * We only offer Dual Seal options for 50Hz pumps. If the user selected 50Hz, select Dual Seal for them.
     */
    private autoSelectSealTypeIf50Hz(): void {
        if (!this.state.fiftyHzSelected) {
            return;
        }

        this.selectSealType(false);
    }

    /**
     * Event Handler for a seal type choice being made. Refer to logic diagram
     * for instruction on how to proceed.
     * @param singleSeal was single seal chosen?
     */
    private selectSealType(singleSeal: boolean): void {
        this.choiceMade({ key: PumpChoiceKeys.SEAL_TYPE, value: singleSeal ? 'Single Seal' : 'Dual Seal' });
        if (singleSeal) {
            this.setState({
                sealTypeChoice: false,
                singleSealSelected: singleSeal,
                solidsHandlingSizeChoice: !this.state.grinderSelected, // if the current pump is not a grinder pump, the next choice to make is solids handling size
                locationChoice: this.state.grinderSelected // if the current pump *is* a grinder pump, the next choice to make is the location
            });
            return;
        }

        // dual seal
        this.setState({
            sealTypeChoice: false,
            singleSealSelected: singleSeal,
            impellerTypeChoice: !this.state.grinderSelected, // if the current pump is not a grinder pump, the next choice to make is impeller type
            locationChoice: this.state.grinderSelected // if the current pump *is* a grinder pump, the next choice to make is the location
        }, () => {
            this.autoSelectSizeForDualSealSolidsHandling();
            this.autoSelectOrdinaryLocationIf50HzGrinder();
        });
    }

    /**
     * We only offer 3" solids handling for dual seal configurations
     */
    private autoSelectSizeForDualSealSolidsHandling(): void {
        if (this.state.grinderSelected || this.state.singleSealSelected) {
            return;
        }

        this.selectSolidsHandlingSize('3"', true);
    }

    /**
     * We only offer Ordinary Location Grinder models in 50Hz configurations.
     */
    private autoSelectOrdinaryLocationIf50HzGrinder(): void {
        if (!this.state.grinderSelected || !this.state.fiftyHzSelected) {
            return;
        }

        this.selectLocation(false);
    }

    /**
     * Event Handler specific to Single Seal Solids Handling pumps, for a
     * solids handling size choice being made. Refer to logic diagram for
     * instruction on how to proceed.
     * @param value solids handling size
     * @param simulated was this event handler simulated?
     */
    private selectSolidsHandlingSize(value: string, simulated: boolean = false): void {
        this.choiceMade({ key: PumpChoiceKeys.SOLIDS_SIZE, value: value });
        if (simulated) {
            return;
        }
        // populate single seal horsepower here
        this.HorsepowerChoices = [
            {
                text: '1.5 hp',
                eventHandler: () => {
                    this.selectHorsepower('1.5 hp');
                }
            },
            {
                text: '2 hp',
                eventHandler: () => {
                    this.selectHorsepower('2 hp');
                }
            },
            {
                text: '3 hp',
                eventHandler: () => {
                    this.selectHorsepower('3 hp');
                }
            },
            {
                text: '5 hp',
                eventHandler: () => {
                    this.selectHorsepower('5 hp');
                }
            }
        ];
        this.setState({
            solidsHandlingSizeChoice: false,
            horsepowerChoice: true
        });
    }

    /**
     * Event Handler specific to Dual Seal Solids Handling pumps, for an
     * impeller type choice being made. Refer to logic diagram for instruction
     * on how to proceed.
     * @param value the chosen impeller type
     */
    private selectImpellerType(value: string): void {
        this.choiceMade({ key: PumpChoiceKeys.IMPELLER_TYPE, value });
        this.setState({
            impellerTypeChoice: false,
            locationChoice: true
        });
        // if frequency is 50hz, select ordinary location
        if (this.state.fiftyHzSelected) {
            this.selectLocation(false);
        }
    }

    /**
     * Event Handler for all Dual Seal pumps, for when a location choice is made.
     * Refer to logic diagram for instruction on how to proceed.
     * @param hazardousLocation is the configured pump an explosion-proof pump?
     */
    private async selectLocation(hazardousLocation: boolean): Promise<void> {
        this.choiceMade({ key: PumpChoiceKeys.LOCATION, value: hazardousLocation ? 'Hazardous' : 'Ordinary' });
        await this.getHPValues(this.state.grinderSelected, hazardousLocation);
        this.setState({
            locationChoice: false,
            horsepowerChoice: true
        }, this.autoSelectHorsepowerForSingleSealGrinder);
    }

    /**
     * We only offer 2.5 hp for single seal grinder pumps.
     */
    private autoSelectHorsepowerForSingleSealGrinder(): void {
        if (!this.state.grinderSelected || !this.state.singleSealSelected) {
            return;
        }

        this.selectHorsepower('2.5 hp');
    }

    /**
     * Event Handler for when a horsepower choice is made. Refer to logic diagram
     * for instruction on how to proceed.
     * @param value the chosen horsepower value
     */
    private selectHorsepower(value: string): void {
        this.choiceMade({ key: PumpChoiceKeys.HORSEPOWER, value: value });
        this.addHighFlowGrinderSeriesOption();
        this.setState({
            horsepowerChoice: false,
            complete: !this.state.grinderSelected,
            grinderSeriesChoice: this.state.grinderSelected
        }, this.tryFireChoicesCompleteEvent);
    }

    /**
     * LGF Series is currently only offered in 5 and 7.5 hp configurations.
     */
    private addHighFlowGrinderSeriesOption(): void {
        this.GrinderSeriesChoices = this.getDefaultGrinderSeries();
        if (!this.state.grinderSelected || this.state.fiftyHzSelected || this.state.singleSealSelected) {
            return;
        }

        const hpChoice = this.getHorsepower();
        if (hpChoice !== Horsepower.FIVE && hpChoice !== Horsepower.SEVEN) {
            return;
        }

        const highFlowSeriesDesc: string = 'LGF High Flow Grinder';
        const highFlowSeries: ChoiceButtonProps = {
            text: highFlowSeriesDesc,
            eventHandler: () => {
                this.selectGrinderSeries(highFlowSeriesDesc);
            }
        };
        let seriesChoices: ChoiceButtonProps[] = this.GrinderSeriesChoices;
        seriesChoices.push(highFlowSeries);
        this.GrinderSeriesChoices = seriesChoices;
    }

    /**
     * Event Handler specific to Grinder pumps, for when a series is selected.
     * Refer to logic guide for how to proceed.
     * @param value the chosen grinder series
     */
    private selectGrinderSeries(value: string): void {
        this.choiceMade({ key: PumpChoiceKeys.GRINDER_SERIES, value: value });
        this.setState({
            grinderSeriesChoice: false,
            complete: true
        }, this.tryFireChoicesCompleteEvent);
    }

    /**
     * If the choice selection is complete, fire the event to let the App know.
     */
    private tryFireChoicesCompleteEvent(): void {
        if (!this.state.complete) {
            return;
        }

        this.props.choicesComplete?.(this.state.complete, this.getViewModel());
    }

    /**
     * Update the list of choices made with the given Choice.
     * @param choice newest choice made
     */
    private choiceMade(choice: Choice): void {
        const temp = this.state.pumpChoices;
        temp.push(choice);
        this.setState({
            pumpChoices: temp
        });
    }

    /**
     * Cut directly to a given spot in the logic diagram.
     * @param choice the choice to jump to in the selection path
     */
    private navigateTo(choice: Choice): void {
        const index: number = this.state.pumpChoices.findIndex(i => i.key === choice.key);
        const temp: Choice[] = this.state.pumpChoices.slice(0, index);
        this.setState({
            frequencyChoice: choice.key === PumpChoiceKeys.FREQUENCY,
            pumpTypeChoice: choice.key === PumpChoiceKeys.PUMP_TYPE,
            sealTypeChoice: choice.key === PumpChoiceKeys.SEAL_TYPE,
            solidsHandlingSizeChoice: choice.key === PumpChoiceKeys.SOLIDS_SIZE,
            impellerTypeChoice: choice.key === PumpChoiceKeys.IMPELLER_TYPE,
            locationChoice: choice.key === PumpChoiceKeys.LOCATION,
            horsepowerChoice: choice.key === PumpChoiceKeys.HORSEPOWER,
            grinderSeriesChoice: choice.key === PumpChoiceKeys.GRINDER_SERIES,
            grinderSelected: (choice.key === PumpChoiceKeys.FREQUENCY || choice.key === PumpChoiceKeys.PUMP_TYPE) ? false : this.state.grinderSelected,
            singleSealSelected: (choice.key === PumpChoiceKeys.FREQUENCY || choice.key === PumpChoiceKeys.PUMP_TYPE || choice.key === PumpChoiceKeys.SEAL_TYPE) ? false : this.state.singleSealSelected,
            fiftyHzSelected: choice.key === PumpChoiceKeys.FREQUENCY ? false : this.state.fiftyHzSelected,
            complete: false,
            pumpChoices: temp
        }, () => { this.props.choicesComplete?.(this.state.complete) });
    }

    /**
     * Convert all choices made into an LEP Pump
     * @returns an object representing a partially-built LEP Pump.
     */
    private getViewModel(): LEPPumpViewModel {
        const viewModel: LEPPumpViewModel = new LEPPumpViewModel();
        const pumpType: string = this.getPumpType();
        const locationText: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.LOCATION)?.value;
        const sealTypeText: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.SEAL_TYPE)?.value;
        const horsepowerText: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.HORSEPOWER)?.value;
        const grinderSeriesText: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.GRINDER_SERIES)?.value;
        viewModel.pumpType = pumpType;
        viewModel.pumpTypeDesc = this.state.grinderSelected ? (grinderSeriesText ? grinderSeriesText : '') : `${pumpType} ${locationText}`;
        viewModel.sealType = sealTypeText ? sealTypeText : '';
        viewModel.horsepower = this.getHorsepower();
        viewModel.horsepowerDesc = horsepowerText ? horsepowerText : '';
        viewModel.frequency = this.state.fiftyHzSelected ? Frequency.B : Frequency.A;
        return viewModel;
    }

    /**
     * Use BridgeAPI to get horsepower values
     * @param grinder should we get HP values for a grinder pump?
     * @param hazardousLocation should we get HP values for an explosion-proof pump?
     */
    private async getHPValues(grinder: boolean, hazardousLocation: boolean): Promise<void> {
        const mapHPValues = async (response: Promise<PumpProperty[]>): Promise<void> => {
            const hpChoices: ChoiceButtonProps[] = [];
            await response.then(r => {
                r = r.sort((a: PumpProperty, b: PumpProperty) => {
                    if (a.Code === null) {
                        return -1;
                    }
                    if (b.Code == null) {
                        return 1;
                    }
                    return a.Code.localeCompare(b.Code);
                });
                for (let choice of r) {
                    if (choice.Code === null || choice.Description === null) {
                        continue;
                    }
                    const hp: ChoiceButtonProps = {
                        text: choice.Description,
                        eventHandler: () => {
                            this.selectHorsepower(choice.Description!);
                        }
                    };
                    hpChoices.push(hp);
                }
            }).then(_ => this.HorsepowerChoices = hpChoices);
        }

        let apiResponse: Promise<PumpProperty[]>;
        if (grinder) {
            apiResponse = getGrinderHPValues(hazardousLocation);
        } else {
            apiResponse = getSolidsHandlingHPValues();
        }
        await mapHPValues(apiResponse);
    }

    /**
     * Evaluate the pump choices to determine what kind of pump this is.
     * @returns a string containing the series of the pump.
     */
    private getPumpType(): string {
        if (this.state.grinderSelected) {
            const temp: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.GRINDER_SERIES)?.value.substring(0, 3);
            if (this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.LOCATION)?.value === 'Hazardous') {
                return `X${temp}`;
            }
            return `${temp}`;
        }
        // solids handling
        if (this.state.singleSealSelected) {
            if (this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.SOLIDS_SIZE)?.value === '3"') {
                return 'LEV';
            }
            // 2.5"
            return 'LE';
        }
        // dual seal
        const impellerType: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.IMPELLER_TYPE)?.value;
        let pumpType: string = '';
        switch (impellerType) {
            case 'MonoVane':
                pumpType = 'LM';
                break;
            case 'Vortex':
                pumpType = 'LV';
                break;
            case '2-Vane':
                pumpType = 'LE';
                break;
        }
        if (this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.LOCATION)?.value === 'Hazardous') {
            return `X${pumpType}`;
        }
        return pumpType;
    }

    /**
     * Get the enumerated horsepower value based on the current pump choices.
     * @returns a Horsepower value
     */
    private getHorsepower(): Horsepower {
        const hp: string | undefined = this.state.pumpChoices.find(c => c.key === PumpChoiceKeys.HORSEPOWER)?.value;
        switch (hp) {
            case '1.5 hp':
                return Horsepower.ONE;
            case '2 hp':
            case '2.5 hp':
                return Horsepower.TWO;
            case '3 hp':
                return Horsepower.THREE;
            case '5 hp':
                return Horsepower.FIVE;
            case '7 hp':
            case '7.5 hp':
                return Horsepower.SEVEN;
            case '10 hp':
                return Horsepower.TEN;
            case '15 hp':
                return Horsepower.FIFTEEN;
            default:
                return Horsepower.TWO;
        }
    }

    render() {
        return (
            <>
                {/* Only show the breadcrumb if more than 1 choice has been made and the hideBreadcrumb flag is not set */}
                {this.state.pumpChoices.length > 1 && !this.props.hideBreadcrumb ? <Breadcrumb choices={this.state.pumpChoices} clicked={this.navigateTo} /> : null}
                {this.state.frequencyChoice ? <PumpChoice titleText='Select Frequency' buttonProps={this.FrequencyButtonChoices} /> : null}
                {this.state.pumpTypeChoice ? <PumpChoice titleText='Select Pump Type' buttonProps={this.PumpTypeChoices} /> : null}
                {this.state.sealTypeChoice ? <PumpChoice titleText='Select Seal Type' buttonProps={this.SealTypeChoices} /> : null}
                {this.state.solidsHandlingSizeChoice ? <PumpChoice titleText='Select Solids Handling Size' buttonProps={this.SolidsHandlingSizeChoices()} /> : null}
                {this.state.impellerTypeChoice ? <PumpChoice titleText='Select Impeller Type' buttonProps={this.ImpellerTypeChoices} /> : null}
                {this.state.locationChoice ? <PumpChoice titleText='Select Location' buttonProps={this.LocationChoices} /> : null}
                {this.state.horsepowerChoice ? <PumpChoice titleText='Select Horsepower' buttonProps={this.HorsepowerChoices} /> : null}
                {this.state.grinderSeriesChoice ? <PumpChoice titleText='Select Series' buttonProps={this.GrinderSeriesChoices} /> : null}
            </>
        );
    }
};


interface ChoiceProps {
    titleText: string,
    buttonProps: ChoiceButtonProps[]
}

interface ChoiceButtonProps {
    text: string,
    eventHandler: () => void
}

/**
 * The PumpChoice function component will display buttons to the user with all
 * available options for the current choice to be made.
 */
const PumpChoice = (props: ChoiceProps): JSX.Element => {
    const colSize: number = parseInt((12 / props.buttonProps.length).toString());
    return (
        <div id='pump-choice-selector'>
            <div className='bap-row'>
                <div className='bap-col-12 text-center'>
                    <h4>{props.titleText}</h4>
                </div>
            </div>
            <div className='bap-row'>
                {props.buttonProps.map((buttonProp, index) => (
                    <div key={index} className={`bap-col-${colSize}`}>
                        <Button cssClass='btn btn-pump-choice' clicked={buttonProp.eventHandler}>{buttonProp.text}</Button>
                    </div>
                ))}
            </div>
        </div>
    );
};
